import { db } from "../../config/main_config";
import { extractAssIDNumber } from './extractAssID.util'

/**
 * The function `getRecentAssuranceTasks` retrieves recent assurance tasks for a specified company
 * within a certain timeframe, with an option to filter completed tasks.
 * @param companyID - The `companyID` parameter in the `getRecentAssuranceTasks` function represents
 * the unique identifier of the company for which you want to retrieve recent assurance tasks. This ID
 * is used to locate the specific company's compliance data in the database.
 * @param [months=3] - The `months` parameter in the `getRecentAssuranceTasks` function specifies the
 * number of months in the past from the current date to consider when retrieving recent assurance
 * tasks. By default, it is set to 3 months, but you can customize this value when calling the
 * function.
 * @param [completedOnly=false] - The `completedOnly` parameter in the `getRecentAssuranceTasks`
 * function is a boolean flag that determines whether only completed tasks should be included in the
 * result. If `completedOnly` is set to `true`, only tasks with a status of 'complete' will be added to
 * the `ass
 * @returns The function `getRecentAssuranceTasks` returns an array of unique assurance tasks that meet
 * the specified criteria. Each task in the array includes the following properties: `taskID`, `name`,
 * `status`, and `other` details.
 */

/**
 * The function `getRecentAssuranceTasks` retrieves recent assurance tasks for a specified company
 * within a certain timeframe, with an option to filter completed tasks.
 */
export const getRecentAssuranceTasks = async function (
    companyID,
    months = 3,
    completedOnly = false
) {
    try {
        const assurance = [];

        // Fetch company name
        const companyDoc = await db.collection('customers').doc(companyID).get();
        if (!companyDoc.exists) {
            console.error(
                `[Utilities] (getRecentAssuranceTasks) Company with ID ${companyID} does not exist.`
            );
            // Always return an array so callers can safely .map()
            return [];
        }

        const companyData = companyDoc.data() || {};
        const companyName = companyData.customerName || 'the customer';
        console.log(
            `[Utilities] (getRecentAssuranceTasks) Company Name: ${companyName}`
        );

        // Fetch assurance data
        const assuranceDoc = await db
            .collection('customers')
            .doc(companyID)
            .collection('compliance')
            .doc('assurance')
            .get();

        if (!assuranceDoc.exists) {
            console.error(
                `[Utilities] (getRecentAssuranceTasks) Assurance document does not exist for company ID ${companyID}.`
            );
            // No assurance data, but still return a consistent type
            return [];
        }

        const data = assuranceDoc.data() || {};

        // Get current date and past date
        const currentDate = new Date();
        const pastDate = new Date();
        pastDate.setMonth(pastDate.getMonth() - months);

        try {
            for (const itemKey in data) {
                const item = data[itemKey];
                if (!item) continue;

                // Only active checks
                if (item?.checkDetail?.active !== 'true') continue;

                const freq = item.checkFrequency || {};
                const nextCheckSource = freq.nextCheck;

                // Guard against missing/invalid nextCheck
                if (!nextCheckSource || typeof nextCheckSource.toDate !== 'function') {
                    continue;
                }

                let nextCheck;
                try {
                    nextCheck = nextCheckSource.toDate();
                } catch (e) {
                    console.error(
                        `[Utilities] (getRecentAssuranceTasks) Invalid nextCheck for item ${itemKey}: ${e.message}`,
                        e
                    );
                    continue;
                }

                const checkDaysFB = parseInt(freq.checkDays, 10) || 0;

                // Calculate lastCheck date by subtracting checkDaysFB from nextCheck
                const lastCheck = new Date(nextCheck);
                lastCheck.setDate(lastCheck.getDate() - checkDaysFB);

                // Check if nextCheck or lastCheck falls within the specified date range
                const inWindow =
                    (nextCheck >= pastDate && nextCheck <= currentDate) ||
                    (lastCheck >= pastDate && lastCheck <= currentDate);

                if (!inWindow) continue;

                const history = item.checkHistory || {};
                for (const historyKey in history) {
                    const historyItem = history[historyKey];
                    if (!historyItem) continue;
                    if (!historyItem.check) continue;
                    if (!historyItem.checkStatus) continue;

                    const isComplete =
                        historyItem.checkStatus.toLowerCase() === 'complete';
                    const status = isComplete ? 'Complete' : 'Incomplete';

                    if (completedOnly && !isComplete) continue;

                    // Always pass a string to extractAssIDNumber to avoid `.match` on undefined
                    const taskID = String(item.assID || itemKey || '');

                    // Safely build the task name
                    const rawName =
                        (item.checkDetail && item.checkDetail.check) || '';
                    const name = rawName
                        ? rawName.replace(
                              /\bAcme,?\s+Inc\.?\b/gi,
                              companyName
                          )
                        : `Assurance task ${taskID}`;

                    assurance.push({
                        taskID,
                        name,
                        status,
                        nextCheck,
                    });
                }
            }
        } catch (error) {
            console.error(
                `[Utilities] (getRecentAssuranceTasks) Error processing assurance data: ${error.message}`,
                error
            );
        }

        // Deduplicate based on 'taskID'
        const uniqueAssuranceMap = new Map();
        assurance.forEach((ass) => {
            if (!ass.taskID) return;
            if (!uniqueAssuranceMap.has(ass.taskID)) {
                uniqueAssuranceMap.set(ass.taskID, ass);
            }
        });
        const uniqueAssurance = Array.from(uniqueAssuranceMap.values());

        // Sorting Logic:
        // 1. Incomplete tasks before complete
        // 2. Within each status, sort by numeric part of assID ascending
        uniqueAssurance.sort((a, b) => {
            const statusA = (a.status || '').toLowerCase();
            const statusB = (b.status || '').toLowerCase();

            if (statusA !== statusB) {
                return statusA === 'incomplete' ? -1 : 1;
            }

            const idA = String(a.taskID || '');
            const idB = String(b.taskID || '');

            const numA = extractAssIDNumber(idA);
            const numB = extractAssIDNumber(idB);

            const isNaNA = Number.isNaN(numA);
            const isNaNB = Number.isNaN(numB);

            if (isNaNA && isNaNB) return 0;
            if (isNaNA) return 1;
            if (isNaNB) return -1;

            return numA - numB;
        });

        // Always return an array so callers can safely do `.map(...)`
        return uniqueAssurance;
    } catch (error) {
        console.error(
            `[Utilities] (getRecentAssuranceTasks) Error fetching recent assurance tasks: ${error.message}`,
            error
        );
        // Keep the return type consistent even on errors
        return [];
    }
};